import type {
  Challenge,
  ChallengeMeta,
  ChapterStructure,
  LoadedChapter,
  RecapQuestion,
  TrackStructure,
  TrackStructureFull,
} from '@devlearn/shared';
import { jsFundamentals } from './js-fundamentals/track';

/** Track structure only (ids, titles, types, versions). Full content loads per chapter. */
export const tracks: TrackStructureFull[] = [
  jsFundamentals,
  {
    id: 'full-stack-node',
    slug: 'full-stack-node',
    title: 'Full Stack with Node',
    shortTitle: 'Full Stack',
    position: 2,
    status: 'coming_soon',
    chapters: [],
  },
  {
    id: 'system-design',
    slug: 'system-design',
    title: 'System Design',
    shortTitle: 'System Design',
    position: 3,
    status: 'coming_soon',
    chapters: [],
  },
  {
    id: 'aws-in-practice',
    slug: 'aws-in-practice',
    title: 'AWS in Practice',
    shortTitle: 'AWS',
    position: 4,
    status: 'coming_soon',
    chapters: [],
  },
];

// Static import() expressions so bundlers emit one chunk per chapter.
const chapterLoaders: Record<string, () => Promise<{ default: Challenge[] }>> = {
  'js-fundamentals/values': () => import('./js-fundamentals/01-values/content'),
  'js-fundamentals/functions': () => import('./js-fundamentals/02-functions/content'),
  'js-fundamentals/async': () => import('./js-fundamentals/03-async/content'),
  'js-fundamentals/objects': () => import('./js-fundamentals/04-objects/content'),
  'js-fundamentals/arrays': () => import('./js-fundamentals/05-arrays/content'),
  'js-fundamentals/modules': () => import('./js-fundamentals/06-modules/content'),
};

const recapLoaders: Record<string, () => Promise<{ default: RecapQuestion[] }>> = {
  'js-fundamentals/values': () => import('./js-fundamentals/01-values/recap'),
  'js-fundamentals/functions': () => import('./js-fundamentals/02-functions/recap'),
  'js-fundamentals/async': () => import('./js-fundamentals/03-async/recap'),
  'js-fundamentals/objects': () => import('./js-fundamentals/04-objects/recap'),
  'js-fundamentals/arrays': () => import('./js-fundamentals/05-arrays/recap'),
  'js-fundamentals/modules': () => import('./js-fundamentals/06-modules/recap'),
};

export class ContentNotFoundError extends Error {
  override name = 'ContentNotFoundError';
}

export function getTrack(trackId: string): TrackStructureFull | undefined {
  return tracks.find((t) => t.id === trackId || t.slug === trackId);
}

export function getChapter(trackId: string, chapterSlug: string): ChapterStructure | undefined {
  return getTrack(trackId)?.chapters.find((c) => c.slug === chapterSlug || c.id === chapterSlug);
}

export type ChallengeLocation = {
  track: TrackStructureFull;
  chapter: ChapterStructure;
  challenge: ChallengeMeta;
  /** 1-based position in the chapter. */
  number: number;
};

export function findChallengeBySlugs(
  trackSlug: string,
  chapterSlug: string,
  challengeSlug: string,
): ChallengeLocation | undefined {
  const track = getTrack(trackSlug);
  const chapter = track?.chapters.find((c) => c.slug === chapterSlug);
  const index = chapter?.challenges.findIndex((c) => c.slug === challengeSlug) ?? -1;
  if (!track || !chapter || index < 0) return undefined;
  return { track, chapter, challenge: chapter.challenges[index]!, number: index + 1 };
}

export function findChallengeById(challengeId: string): ChallengeLocation | undefined {
  for (const track of tracks) {
    for (const chapter of track.chapters) {
      const index = chapter.challenges.findIndex((c) => c.id === challengeId);
      if (index >= 0) return { track, chapter, challenge: chapter.challenges[index]!, number: index + 1 };
    }
  }
  return undefined;
}

/** The minimal structure the unlock rules need. */
export function toRuleStructure(track: TrackStructureFull): TrackStructure {
  return {
    chapters: track.chapters.map((ch) => ({
      id: ch.id,
      challenges: ch.challenges.map((c) => ({ id: c.id, type: c.type, isGuest: c.isGuest })),
    })),
  };
}

export async function loadChapter(trackId: string, chapterSlug: string): Promise<LoadedChapter> {
  const structure = getChapter(trackId, chapterSlug);
  const loader = structure && chapterLoaders[`${getTrack(trackId)!.id}/${structure.slug}`];
  if (!structure || !loader) throw new ContentNotFoundError(`Unknown chapter ${trackId}/${chapterSlug}`);
  const { default: challenges } = await loader();
  const { challenges: _meta, ...rest } = structure;
  return { ...rest, challenges };
}

export async function loadRecap(trackId: string, chapterSlug: string): Promise<RecapQuestion[]> {
  const structure = getChapter(trackId, chapterSlug);
  const loader = structure && recapLoaders[`${getTrack(trackId)!.id}/${structure.slug}`];
  if (!structure || !loader) throw new ContentNotFoundError(`Unknown recap ${trackId}/${chapterSlug}`);
  return (await loader()).default;
}

/** All challenge metas across all tracks, in order. */
export function allChallenges(): ChallengeLocation[] {
  return tracks.flatMap((track) =>
    track.chapters.flatMap((chapter) =>
      chapter.challenges.map((challenge, i) => ({ track, chapter, challenge, number: i + 1 })),
    ),
  );
}

export { chapterLoaders as _chapterLoaders, recapLoaders as _recapLoaders };
